import { create } from "zustand";
import type { SampleSourceId } from "@/data/realData/sampleRegistry";
import type { MZRTData } from "@/types/spectrum";

/**
 * TIC 브러시 선택 구간 — **RT 도메인**(분). 2026-09-03 변경.
 *
 * 종전에는 `{startIndex, endIndex}` 로 TIC 배열 인덱스를 저장했다. 인덱스는
 * 배열이 어떻게 만들어졌는지(전량인지 축약인지, 축약이면 어떤 규칙인지)에
 * 따라 의미가 바뀌는 좌표라, 표시 방식을 바꾸는 순간 같은 숫자가 다른 구간을
 * 가리켰다(외부 자문 2026-09-03: "상호작용 좌표 계약 부재").
 * RT 는 원자료의 물리량이므로 렌더러·표시 해상도와 무관하게 같은 구간을
 * 가리킨다.
 */
export interface BrushRange {
  rtMin: number;
  rtMax: number;
}

/**
 * 20 samples 전체 — sampleRegistry.ts SampleSourceId와 동일.
 * NOTE: 데이터 출처 selector 제거 후, mockup fallback 함수 시그니처에서만 참조.
 */
export type SampleSource = SampleSourceId;

interface AnalysisStore {
  /**
   * 아래 상호작용 상태가 **어느 분석의 것인지**. 2026-08-28 추가.
   *
   * 이 store 는 모듈 전역이라 라우트를 옮겨도 살아남는데, 그때까지 상태에는
   * 소속 분석이 없었다. 그래서 분석 A 에서 고른 `feature_id` 가 분석 B 로
   * 넘어가 B 의 다른 물질이 선택된 것처럼 보였다 — feature_id 는 분석마다
   * `F1`·`F2`… 로 재사용되므로 번호가 겹치는 일이 흔하다.
   */
  analysisId: number | null;
  /** 이 분석에서 진입 자동 선택을 이미 걸었는가. `enterAnalysis` 가 초기화한다. */
  autoSelectDone: boolean;

  // Interaction state
  selectedFeatureId: string | null;
  selectedRT: number | null;
  brushRange: BrushRange | null;
  hoveredFeatureId: string | null;

  // Actions
  /**
   * 분석 화면 진입. **다른 분석이면** 이전 분석의 상호작용 상태를 전부 버린다.
   * 같은 분석이면 아무것도 하지 않는다 — 화면을 잠깐 벗어났다 돌아온 사람의
   * 선택을 지우지 않기 위해서다(상태가 store 에 있어 언마운트를 살아남는다).
   */
  enterAnalysis: (analysisId: number) => void;
  /** 진입 자동 선택을 걸었다고 표시. 이후 해제해도 화면이 다시 고르지 않는다. */
  markAutoSelectDone: () => void;
  toggleFeature: (featureId: string) => void;
  selectFeatureWithRT: (featureId: string, rt: number | null) => void;
  setSelectedRT: (rt: number | null) => void;
  /**
   * 기록된 관찰이 없는 스캔을 고른다 — RT 만 옮기고 물질 선택은 해제한다.
   *
   * 2026-09-03: 종전에는 RT 만 바꿔 물질 선택이 남았고, MS 패널과 선택
   * 물질 카드가 **서로 다른 스캔**을 가리켰다. 선택을 비워야 참조 순위
   * 패널이 스캔 앵커로 전환돼 '분석되지 않은 구간의 순위'를 볼 수 있다.
   */
  selectScanOnly: (rt: number | null) => void;
  selectMZRT: (data: MZRTData) => void;
  setBrushRange: (range: BrushRange | null) => void;
  clearBrush: () => void;
  setHoveredFeature: (featureId: string | null) => void;
  /** Reset all interaction state to initial values. Use in test beforeEach. */
  reset: () => void;
}

export const useAnalysisStore = create<AnalysisStore>((set) => ({
  analysisId: null,
  autoSelectDone: false,
  selectedFeatureId: null,
  selectedRT: null,
  brushRange: null,
  hoveredFeatureId: null,

  enterAnalysis: (analysisId) =>
    set((state) =>
      state.analysisId === analysisId
        ? {}
        : {
            analysisId,
            autoSelectDone: false,
            selectedFeatureId: null,
            selectedRT: null,
            brushRange: null,
            hoveredFeatureId: null,
          },
    ),

  markAutoSelectDone: () => set({ autoSelectDone: true }),

  toggleFeature: (featureId) =>
    set((state) => ({
      selectedFeatureId:
        state.selectedFeatureId === featureId ? null : featureId,
    })),

  selectFeatureWithRT: (featureId, rt) =>
    set((state) => {
      const isToggleOff = state.selectedFeatureId === featureId;
      return {
        selectedFeatureId: isToggleOff ? null : featureId,
        selectedRT: isToggleOff ? state.selectedRT : (rt ?? state.selectedRT),
      };
    }),

  setSelectedRT: (rt) => set({ selectedRT: rt }),

  selectScanOnly: (rt) => set({ selectedFeatureId: null, selectedRT: rt }),

  // 2026-09-03 (운영자 결정 7-a): 동정되지 않은 측정점(feature_id="")을 누르면
  // **선택을 해제한다** — TIC 의 빈 구간 클릭(`selectScanOnly`)과 같은 정책이다.
  //
  // 종전에는 기존 선택을 보존했고(Sprint 22), 그래서 회색 점을 누르면 화면 네
  // 곳이 서로 다른 것을 가리켰다(실측): 질량 스펙트럼만 새 스캔(RT 4.447)으로
  // 가고 표·TIC·상세 패널은 옛 후보(RT 11.6935 Naphthalene)에 남았다.
  //
  // 같은 상황을 두고 코드 주석이 서로 반대였다 — 이 자리의 옛 주석은 "보존이
  // 의도"라 했고, TIC 경로 주석은 같은 상황을 결함으로 규정하고 해제로 바꿨다.
  // 코드만으로는 정본을 정할 수 없어 운영자가 결정했다. 두 플롯이 한 정책을
  // 쓰므로 이제 어느 쪽을 눌러도 화면이 같은 스캔을 가리킨다.
  selectMZRT: (data) =>
    set(() => ({
      selectedRT: data.rt,
      selectedFeatureId: data.feature_id || null,
    })),

  setBrushRange: (range) => set({ brushRange: range }),

  clearBrush: () => set({ brushRange: null }),

  setHoveredFeature: (featureId) => set({ hoveredFeatureId: featureId }),

  reset: () =>
    set({
      analysisId: null,
      autoSelectDone: false,
      selectedFeatureId: null,
      selectedRT: null,
      brushRange: null,
      hoveredFeatureId: null,
    }),
}));
